import React, { useState, useEffect, useRef } from "react";
import { parseMarkdown, downloadMarkdown, downloadHtml } from "../utils/utils";
import { getAuthHeaders } from "../../auth/services/getAuthHeaders";
import {
  ClipboardList,
  X,
  XCircle,
  Save,
  Mail,
  Copy,
  Check,
  Download,
  FileCode,
} from "lucide-react";

interface SummaryModalProps {
  isOpen: boolean;
  onClose: () => void;
  meetingTranscript: { timestamp: string; orig: string; tran: string; isMic?: boolean }[];
  currentMeetingId: string | null;
  /** Base URL of the summary-service (e.g. POST /summarize, POST /meeting/email) */
  summaryServiceUrl: string;
  targetLang: string;
  targetLangLabel: string;
}

export function SummaryModal({
  isOpen,
  onClose,
  meetingTranscript,
  currentMeetingId,
  summaryServiceUrl,
  targetLang,
  targetLangLabel,
}: SummaryModalProps) {
  const [summaryText, setSummaryText] = useState("");
  const [s3Url, setS3Url] = useState("");
  const [htmlFilename, setHtmlFilename] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Email state
  const [emailInput, setEmailInput] = useState("");
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailStatus, setEmailStatus] = useState<"idle" | "success" | "error">("idle");
  const [emailMessage, setEmailMessage] = useState("");

  // Copy state
  const [copied, setCopied] = useState(false);

  // Avoid running duplicate requests in React strict mode
  const fetchInProgress = useRef(false);
  // Polling ref for 504 fallback
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const safetyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Polling UI state
  const [isPolling, setIsPolling] = useState(false);

  useEffect(() => {
    if (isOpen && meetingTranscript.length > 0 && !summaryText && !loading) {
      generateSummary();
    }
  }, [isOpen]);

  // Reset state when closed
  useEffect(() => {
    if (!isOpen) {
      setSummaryText("");
      setS3Url("");
      setHtmlFilename("");
      setError("");
      setEmailInput("");
      setEmailStatus("idle");
      setEmailMessage("");
      setIsPolling(false);
      fetchInProgress.current = false;
      // Clean up polling on close
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
      if (safetyTimeoutRef.current) clearTimeout(safetyTimeoutRef.current);
    }
  }, [isOpen]);

  const compileTranscriptText = () => {
    return meetingTranscript
      .map((t) => `[${t.timestamp}] Origen: "${t.orig}" | Traducción: "${t.tran}"`)
      .join("\n");
  };

  /** Polls GET /summarize/{meeting_id}/status every 5s until completed or failed */
  const pollStatus = async (meetingId: string | number) => {
    setIsPolling(true);
    setLoading(true);
    setError("");

    pollIntervalRef.current = setInterval(async () => {
      try {
        const authHeaders = await getAuthHeaders();
        const res = await fetch(`${summaryServiceUrl}/summarize/${meetingId}/status`, {
          headers: authHeaders,
        });
        if (!res.ok) return; // keep polling on transient errors

        const data = await res.json();

        if (data.status === "completed") {
          clearInterval(pollIntervalRef.current!);
          if (safetyTimeoutRef.current) clearTimeout(safetyTimeoutRef.current);
          setSummaryText(data.summary || "No se recibió respuesta del resumen.");
          setS3Url(data.s3_url || "");
          setHtmlFilename(data.html_filename || "");
          setIsPolling(false);
          setLoading(false);
        } else if (data.status === "failed") {
          clearInterval(pollIntervalRef.current!);
          if (safetyTimeoutRef.current) clearTimeout(safetyTimeoutRef.current);
          setError(data.error || "Fallo interno al generar la minuta.");
          setIsPolling(false);
          setLoading(false);
        }
        // "pending" / "processing" → keep polling
      } catch (err) {
        console.warn("Error consultando el status de la minuta", err);
      }
    }, 5000); // cada 5 segundos

    // Safety timeout: Lambda max execution is 15 min
    safetyTimeoutRef.current = setTimeout(() => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        setError("Tiempo de espera agotado. La minuta tardó más de 15 minutos.");
        setIsPolling(false);
        setLoading(false);
      }
    }, 15 * 60 * 1000);
  };

  const generateSummary = async () => {
    if (fetchInProgress.current) return;
    fetchInProgress.current = true;
    setLoading(true);
    setError("");

    const transcriptText = compileTranscriptText();

    try {
      const authHeaders = await getAuthHeaders();
      const response = await fetch(`${summaryServiceUrl}/summarize`, {
        method: "POST",
        headers: { ...authHeaders, "Content-Type": "application/json" },
        body: JSON.stringify({
          transcript: transcriptText,
          meeting_id: currentMeetingId,
          format_type: `minuta de reunión en ${targetLangLabel}`,
          target_lang: targetLang,
        }),
      });

      if (!response.ok) {
        // 504/503/502 → API Gateway timeout, Lambda sigue corriendo → polling
        if (response.status === 504 || response.status === 503 || response.status === 502) {
          if (currentMeetingId) pollStatus(currentMeetingId);
          return;
        }
        throw new Error(`Error del servidor: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();

      // Guard against empty body (Serverless Offline emulator bug)
      if (!data.summary && currentMeetingId) {
        pollStatus(currentMeetingId);
        return;
      }

      // Terminó en < 29s → renderizar directamente
      setSummaryText(data.summary || "No se recibió respuesta del resumen.");
      setS3Url(data.s3_url || "");
      setHtmlFilename(data.html_filename || "");
    } catch (err: any) {
      // Cierre de conexión por timeout de red → polling
      if (err.message?.includes("fetch") || err.message?.includes("Network") || err.message?.includes("Failed")) {
        if (currentMeetingId) { pollStatus(currentMeetingId); return; }
      }
      console.error(err);
      setError(err.message || "Error al conectar con el servidor.");
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    if (!summaryText) return;
    try {
      await navigator.clipboard.writeText(summaryText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // Fallback copy method
      const textArea = document.createElement("textarea");
      textArea.value = summaryText;
      textArea.style.position = "fixed";
      textArea.style.left = "-9999px";
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      const success = document.execCommand("copy");
      document.body.removeChild(textArea);
      if (success) {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } else {
        alert("No se pudo copiar automáticamente.");
      }
    }
  };

  const handleSendEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    const emails = emailInput
      .split(",")
      .map((e) => e.trim())
      .filter((e) => e);

    if (emails.length === 0) {
      setEmailStatus("error");
      setEmailMessage("Por favor ingresa al menos un correo.");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    for (const email of emails) {
      if (!emailRegex.test(email)) {
        setEmailStatus("error");
        setEmailMessage(`El correo "${email}" no es válido.`);
        return;
      }
    }

    setEmailLoading(true);
    setEmailStatus("idle");

    try {
      const authHeaders = await getAuthHeaders();
      const response = await fetch(`${summaryServiceUrl}/meeting/email`, {
        method: "POST",
        headers: { ...authHeaders, "Content-Type": "application/json" },
        body: JSON.stringify({
          meeting_id: currentMeetingId,
          email: emails.join(","),
        }),
      });

      if (!response.ok) {
        throw new Error("Error en la llamada de email del servidor");
      }

      setEmailStatus("success");
      setEmailMessage(`¡Minuta enviada correctamente a ${emails.join(", ")}!`);
      setEmailInput("");
    } catch (err) {
      console.error(err);
      setEmailStatus("error");
      setEmailMessage("No se pudo enviar el correo. Revisa la configuración del servidor.");
    } finally {
      setEmailLoading(false);
      setTimeout(() => {
        setEmailStatus("idle");
        setEmailMessage("");
      }, 5000);
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black/60 backdrop-blur-md z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div 
        className="bg-[var(--window-bg)] border border-[var(--border)] rounded-xl max-w-3xl w-full max-h-[85vh] shadow-2xl flex flex-col overflow-hidden animate-[modal-appear_0.22s_cubic-bezier(0.16,1,0.3,1)] text-[var(--text)]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 bg-[var(--surface)] border-b border-[var(--border)] flex-shrink-0">
          <div className="flex items-center gap-2">
            <ClipboardList size={18} className="text-[var(--text-dim)]" />
            <h2 className="text-sm font-semibold text-[var(--text)] uppercase tracking-wider font-mono">
              Minuta & Resumen de Reunión (IA)
            </h2>
          </div>
          <button 
            onClick={onClose}
            className="w-6 h-6 rounded-md bg-[var(--surface)] hover:bg-[var(--surface-hover)] border border-[var(--border)] text-[var(--text-muted)] hover:text-[var(--text)] flex items-center justify-center transition-all"
          >
            <X size={14} />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 overflow-y-auto flex-1 scrollbar-thin scrollbar-thumb-white/10 scrollbar-track-transparent">
          {loading && (
            <div className="flex flex-col items-center justify-center gap-4 py-20 text-center">
              <div className="w-10 h-10 border-4 border-[var(--accent)]/20 border-t-[var(--accent)] rounded-full animate-spin"></div>
              <p className="font-mono text-xs text-[var(--text-dim)] animate-pulse">
                {isPolling
                  ? "La IA está procesando la minuta..."
                  : "Generando minuta y estructurando acuerdos con IA..."}
              </p>
              {isPolling && (
                <p className="text-[10px] text-[var(--text-dim)] max-w-xs leading-relaxed">
                  La generación tomó más de 29s. La Lambda sigue corriendo en segundo plano — te avisamos cuando termine.
                </p>
              )}
            </div>
          )}

          {error && (
            <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-5 flex flex-col gap-3 text-left">
              <div className="text-red-400 font-bold text-xs font-mono flex items-center gap-2">
                <XCircle size={14} />
                <span>Error al generar la minuta</span>
              </div>
              <p className="text-xs text-[var(--text-muted)] leading-normal">{error}</p>
              <button 
                onClick={generateSummary}
                className="self-start px-3 py-1.5 bg-red-500/20 hover:bg-red-500/30 border border-red-500/30 rounded text-xs text-red-300 font-medium transition-all"
              >
                Reintentar
              </button>
            </div>
          )}

          {!loading && !error && summaryText && (
            <div className="flex flex-col gap-4">
              {/* Local File notice if returned by backend */}
              {htmlFilename && (
                <div className="flex items-center gap-2 px-3 py-2 bg-emerald-500/10 border border-emerald-500/20 rounded-lg text-emerald-400 text-[11px] font-mono">
                  <Save size={13} />
                  <span>
                    Guardada en servidor: <strong>uploads/meetings/{htmlFilename}</strong>
                  </span>
                </div>
              )}

              {/* parsed HTML */}
              <div 
                className="bg-[var(--surface)] border border-[var(--border)] rounded-lg p-5 text-sm overflow-y-auto max-h-[45vh] leading-relaxed select-text"
                dangerouslySetInnerHTML={{ __html: parseMarkdown(summaryText) }}
              />

              {/* Email Alert Banner */}
              {emailMessage && (
                <div 
                  className={`px-4 py-2.5 rounded-lg text-xs leading-normal font-medium ${
                    emailStatus === "success" 
                      ? "bg-emerald-500/10 border border-emerald-500/20 text-emerald-450" 
                      : "bg-red-500/10 border border-red-500/20 text-red-450"
                  }`}
                >
                  {emailMessage}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer actions */}
        {!loading && (summaryText || error) && (
          <div className="px-5 py-4 bg-[var(--surface)] border-t border-[var(--border)] flex flex-col md:flex-row md:items-center justify-between gap-4 flex-shrink-0">
            {/* Email form (if we have a summary and meeting ID) */}
            {summaryText && (
              <form onSubmit={handleSendEmail} className="flex items-center gap-1.5 p-1 bg-[var(--surface)] border border-[var(--border)] rounded-lg w-full md:max-w-xs">
                <input
                  type="text"
                  placeholder="Enviar a otro correo..."
                  value={emailInput}
                  onChange={(e) => setEmailInput(e.target.value)}
                  className="bg-transparent border-none outline-none py-1.5 px-2.5 text-xs flex-1 text-[var(--text)] placeholder-[var(--text-dim)] min-w-0"
                  disabled={emailLoading}
                />
                <button
                  type="submit"
                  disabled={emailLoading}
                  className="px-3 py-1.5 bg-[var(--accent)] hover:bg-[#006ee6] disabled:bg-[var(--surface)] disabled:opacity-50 text-white font-semibold text-xs rounded transition-all whitespace-nowrap min-w-[70px] flex items-center justify-center gap-1.5 cursor-pointer"
                >
                  {emailLoading ? (
                    "Enviando..."
                  ) : (
                    <>
                      <Mail size={12} />
                      <span>Enviar</span>
                    </>
                  )}
                </button>
              </form>
            )}

            {/* General Actions */}
            <div className="flex items-center gap-2.5 justify-end ml-auto">
              {summaryText && (
                <>
                  <button
                    onClick={handleCopy}
                    className="px-3 py-1.5 border border-[var(--border)] bg-[var(--surface)] hover:bg-[var(--surface-hover)] text-[var(--text-muted)] rounded text-xs transition-all active:scale-95 font-medium cursor-pointer flex items-center gap-1.5"
                  >
                    {copied ? (
                      <>
                        <Check size={12} className="text-emerald-500" />
                        <span>Copiado</span>
                      </>
                    ) : (
                      <>
                        <Copy size={12} />
                        <span>Copiar texto</span>
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => downloadMarkdown(summaryText)}
                    className="px-3 py-1.5 border border-[var(--accent)]/25 bg-[var(--accent-muted)] hover:bg-[var(--accent-muted)]/1.5 text-[var(--accent)] rounded text-xs transition-all active:scale-95 font-semibold cursor-pointer flex items-center gap-1.5"
                  >
                    <Download size={12} />
                    <span>Descargar .md</span>
                  </button>
                  <button
                    onClick={() => downloadHtml(summaryText)}
                    className="px-3 py-1.5 border border-emerald-500/25 bg-emerald-500/10 hover:bg-emerald-550/20 text-emerald-600 dark:text-emerald-400 rounded text-xs transition-all active:scale-95 font-semibold cursor-pointer flex items-center gap-1.5"
                  >
                    <FileCode size={12} />
                    <span>Descargar HTML</span>
                  </button>
                </>
              )}
              <button
                onClick={onClose}
                className="px-4 py-1.5 bg-[var(--accent)] hover:bg-[#006ee6] text-white rounded text-xs transition-all active:scale-95 font-semibold cursor-pointer"
              >
                Listo
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
